import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTeam } from "@/context/TeamContext";
import { useUpload } from "@/context/UploadContext";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { House, CalendarBlank, ChartBar, SignOut, SoccerBall, Gear, UploadSimple, X, Eye, PencilSimple, ShieldCheck, CaretDown, Check, UsersThree } from "@phosphor-icons/react";

const ALL_LINKS = [
  { to: "/dashboard", label: "Dashboard", icon: House, testid: "nav-dashboard" },
  { to: "/games", label: "Games", icon: CalendarBlank, testid: "nav-games" },
  { to: "/stats", label: "Stats", icon: ChartBar, testid: "nav-stats" },
  { to: "/settings", label: "Settings", icon: Gear, testid: "nav-settings", ownerOnly: true },
  { to: "/admin", label: "Admin", icon: ShieldCheck, testid: "nav-admin", ownerOnly: true },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { teams, currentTeam, currentTeamId, currentRole, setCurrentTeamId } = useTeam();
  const navigate = useNavigate();
  const { uploads, resumeUpload, cancelUpload } = useUpload();
  const activeUploads = Object.entries(uploads);
  const isViewer = currentRole === "viewer";
  const isMember = currentRole === "member";
  const isOwner = !isViewer && !isMember;
  const links = ALL_LINKS.filter((l) => !l.ownerOnly || isOwner);

  return (
    <div className="min-h-screen bg-[#0A0A0A] grain relative">
      <header className="sticky top-0 z-50 bg-black/60 backdrop-blur-xl border-b border-zinc-800">
        <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <button
            onClick={() => navigate("/dashboard")}
            className="flex items-center gap-2 group"
            data-testid="brand-logo"
          >
            <img src="/logo-icon.png" alt="The Gaffer" className="w-9 h-9 object-contain" />
            <span className="font-heading text-2xl text-white leading-none pt-1">THE <span className="text-[#CCFF00]">GAFFER</span></span>
          </button>

          <nav className="hidden md:flex items-center gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={l.testid}
                className={({ isActive }) =>
                  `font-label text-xs px-4 py-2 flex items-center gap-2 rounded-sm transition-colors duration-150 ${
                    isActive
                      ? "text-[#CCFF00] bg-[#CCFF00]/10"
                      : "text-zinc-400 hover:text-white hover:bg-white/5"
                  }`
                }
              >
                <l.icon size={16} weight="bold" />
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            {teams.length > 1 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid="nav-team-switcher"
                    className="hidden sm:flex items-center gap-1.5 border border-zinc-800 text-zinc-300 hover:text-[#CCFF00] hover:border-[#CCFF00] font-label text-xs px-3 py-2 rounded-sm transition-colors duration-150 max-w-[180px]"
                    title="Switch team"
                  >
                    <UsersThree size={14} weight="bold" className="shrink-0" />
                    <span className="truncate">{currentTeam?.name}</span>
                    <CaretDown size={12} weight="bold" className="shrink-0" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-[#121212] border-zinc-800 text-white min-w-[240px]">
                  {teams.map((t) => (
                    <DropdownMenuItem
                      key={t.id}
                      data-testid={`nav-team-option-${t.id}`}
                      onClick={() => setCurrentTeamId(t.id)}
                      className="cursor-pointer focus:bg-white/5 focus:text-white flex items-center justify-between gap-2"
                    >
                      <span className={`truncate ${t.id === currentTeamId ? "text-[#CCFF00]" : "text-zinc-200"}`}>{t.name}</span>
                      <span className="flex items-center gap-1.5 shrink-0">
                        <span className="font-label text-[9px] uppercase text-zinc-500">{t.my_role}</span>
                        {t.id === currentTeamId && <Check size={14} className="text-[#CCFF00]" weight="bold" />}
                      </span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            {isViewer && (
              <span data-testid="viewer-badge" className="hidden sm:flex items-center gap-1.5 font-label text-[10px] text-[#CCFF00] bg-[#CCFF00]/10 px-2 py-1 rounded-sm">
                <Eye size={12} weight="bold" /> View only
              </span>
            )}
            {isMember && (
              <span data-testid="member-badge" className="hidden sm:flex items-center gap-1.5 font-label text-[10px] text-[#CCFF00] bg-[#CCFF00]/10 px-2 py-1 rounded-sm">
                <PencilSimple size={12} weight="bold" /> Member
              </span>
            )}
            <button
              onClick={() => navigate("/profile")}
              data-testid="current-user-email"
              className="hidden sm:block text-sm text-zinc-400 hover:text-[#CCFF00] truncate max-w-[160px] transition-colors duration-150"
              title="View profile"
            >
              {user?.email}
            </button>
            <button
              onClick={logout}
              data-testid="logout-btn"
              className="p-2 text-zinc-400 hover:text-[#FF3B30] transition-colors duration-150"
              title="Log out"
            >
              <SignOut size={20} weight="bold" />
            </button>
          </div>
        </div>

        {/* mobile nav */}
        <nav className="md:hidden flex items-center gap-1 px-4 pb-3 border-t border-zinc-900 pt-2">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`${l.testid}-mobile`}
              className={({ isActive }) =>
                `flex-1 font-label text-[10px] px-2 py-2 flex flex-col items-center gap-1 rounded-sm ${
                  isActive ? "text-[#CCFF00] bg-[#CCFF00]/10" : "text-zinc-400"
                }`
              }
            >
              <l.icon size={18} weight="bold" />
              {l.label}
            </NavLink>
          ))}
        </nav>
      </header>

      {activeUploads.length > 0 && (
        <div className="sticky top-16 z-40 bg-[#121212] border-b border-zinc-800" data-testid="global-upload-bar">
          {activeUploads.map(([gameId, u]) => {
            const pct = u.total ? Math.min(100, Math.round((u.sent / u.total) * 100)) : 0;
            return (
              <div key={gameId} className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-2 flex items-center gap-3 text-xs" data-testid="global-upload-indicator">
                <UploadSimple size={14} className="text-[#CCFF00] shrink-0" />
                <button
                  type="button"
                  onClick={() => navigate(`/games/${gameId}`)}
                  data-testid="global-upload-goto-btn"
                  className="text-zinc-300 hover:text-white truncate max-w-[220px] text-left font-label"
                >
                  {u.label || u.filename}
                </button>
                <div className="hidden sm:block flex-1 max-w-xs h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                  <div className="h-full bg-[#CCFF00] transition-all duration-300" style={{ width: `${pct}%` }} />
                </div>
                <span className="text-zinc-500 shrink-0 font-label">
                  {u.status === "uploading" && `${pct}%`}
                  {u.status === "finalizing" && "Finishing…"}
                  {u.status === "paused" && "Paused"}
                </span>
                {u.status === "paused" && (
                  <button type="button" onClick={() => resumeUpload(gameId)} data-testid="global-upload-resume-btn" className="text-[#CCFF00] hover:underline shrink-0 font-label">
                    Resume
                  </button>
                )}
                <button type="button" onClick={() => cancelUpload(gameId)} data-testid="global-upload-cancel-btn" className="text-zinc-500 hover:text-[#FF3B30] shrink-0">
                  <X size={14} weight="bold" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      <main className="relative z-10 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>
    </div>
  );
}
